// EngagementDistribution.js
// Teacher home card showing how the class roster splits across three
// engagement bands, from `students[].last_active` (already on
// /home/teacher-summary -- home_routes.py:396-405).
// See docs/superpowers/specs/2026-09-29-home-finish.md §F4c.
import React from "react";
import { Text, StyleSheet } from "react-native";

import { homeColors } from "../homeTheme";
import HomeCard from "../primitives/HomeCard";
import HomeDistributionBar from "../primitives/HomeDistributionBar";
import HomeSkeleton from "../primitives/HomeSkeleton";
import HomeEmpty from "../primitives/HomeEmpty";

const identity = (s) => s;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Pure classification of a roster against `now`: active (<=7 days, matching
 * home_attention.py's `_inactive_items` inactivity threshold exactly),
 * slipping (8-14 days), inactive (>14 days, or `last_active` null/malformed
 * -- a student who has never been active, or whose timestamp cannot be
 * parsed, is treated the same way: inactive, never a thrown error).
 */
export function classifyEngagement(students = [], now = Date.now()) {
  const nowMs = typeof now === "number" ? now : new Date(now).getTime();
  const counts = { active: 0, slipping: 0, inactive: 0 };

  (Array.isArray(students) ? students : []).forEach((student) => {
    const lastActive = student?.last_active;
    if (lastActive == null) {
      counts.inactive += 1;
      return;
    }
    const lastActiveMs = new Date(lastActive).getTime();
    if (Number.isNaN(lastActiveMs)) {
      counts.inactive += 1;
      return;
    }
    const days = (nowMs - lastActiveMs) / DAY_MS;
    if (days <= 7) counts.active += 1;
    else if (days <= 14) counts.slipping += 1;
    else counts.inactive += 1;
  });

  return counts;
}

const EngagementDistribution = ({
  students = [],
  loading = false,
  isDarkMode = false,
  t = identity,
  now,
}) => {
  const colors = homeColors(isDarkMode);
  const counts = classifyEngagement(students, now);
  const total = counts.active + counts.slipping + counts.inactive;
  const legend = t("{active} active · {slipping} slipping · {inactive} inactive", counts);

  return (
    <HomeCard title={t("Class engagement")} isDarkMode={isDarkMode} testID="engagement-distribution">
      {loading ? (
        <HomeSkeleton rows={1} isDarkMode={isDarkMode} testID="engagement-distribution-skeleton" t={t} />
      ) : total === 0 ? (
        <HomeEmpty text={t("No students yet.")} isDarkMode={isDarkMode} />
      ) : (
        <>
          <HomeDistributionBar
            testID="engagement-distribution-bar"
            isDarkMode={isDarkMode}
            accessibilityLabel={legend}
            segments={[
              { key: "active", count: counts.active, color: colors.green },
              { key: "slipping", count: counts.slipping, color: colors.amber },
              { key: "inactive", count: counts.inactive, color: colors.ink3 },
            ]}
          />
          <Text style={[styles.legend, { color: colors.textSecondary }]}>{legend}</Text>
        </>
      )}
    </HomeCard>
  );
};

const styles = StyleSheet.create({
  legend: { fontSize: 12, marginTop: 8 },
});

export default EngagementDistribution;
